import {
  computeFinanceBudget,
  FINANCE_MAX_LABEL_LENGTH,
  FINANCE_MAX_MONTHLY_CENTS,
  FINANZNOTE_SCALE,
  FINANZNOTE_TARGET_PERCENT,
  finanznote,
} from '@embertime/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createRoute, Navigate } from '@tanstack/react-router'
import { ChevronDown, Eye, Plus, Trash2 } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts'
import { VorsorgeTab } from '../components/finance/vorsorge-tab'
import { useMobilePageTitle } from '../components/shell/mobile-shell-outlets'
import { Button } from '../components/ui/button'
import { confirm } from '../components/ui/confirm-dialog'
import { Input } from '../components/ui/input'
import { Select } from '../components/ui/select'
import { api, type FinanceBudgetItem, type FinanceBudgetSection } from '../lib/api'
import { centsToInput, parseCents } from '../lib/finance-input'
import { formatCurrency, formatNumber } from '../lib/format'
import { notify } from '../lib/notify'
import { useThemeColors } from '../lib/theme-colors'
import { cn } from '../lib/utils'
import { Route as rootRoute } from './__root'

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/finanzplanung',
  component: FinanzplanungPage,
})

// Farb-Codierung 1:1 aus dem Original-Workbook („Mandanten-Log II"):
// Fest = rot, Flüssig = gelb, Sicherheit = blau, Vermögensaufbau = grün.
const SECTIONS: Array<{
  key: FinanceBudgetSection
  title: string
  short: string
  tone: 'accent' | 'danger' | 'warning' | 'info' | 'success'
}> = [
  { key: 'income', title: 'Einnahmen', short: 'Einnahmen', tone: 'accent' },
  { key: 'fixed', title: 'Feste Ausgaben', short: 'Fest', tone: 'danger' },
  { key: 'variable', title: 'Flüssige Ausgaben', short: 'Flüssig', tone: 'warning' },
  { key: 'insurance', title: 'Sicherheit (Versicherungen)', short: 'Sicherheit', tone: 'info' },
  { key: 'wealth', title: 'Vermögensaufbau', short: 'Vermögensaufbau', tone: 'success' },
]

const TONE_CLASSES: Record<
  (typeof SECTIONS)[number]['tone'],
  { header: string; title: string; dot: string }
> = {
  accent: { header: 'bg-accent/10', title: 'text-accent', dot: 'bg-accent' },
  danger: { header: 'bg-danger/10', title: 'text-danger', dot: 'bg-danger' },
  warning: { header: 'bg-warning/10', title: 'text-warning', dot: 'bg-warning' },
  info: { header: 'bg-info/10', title: 'text-info', dot: 'bg-info' },
  success: { header: 'bg-success/10', title: 'text-success', dot: 'bg-success' },
}

type FinanzTab = 'haushalt' | 'vorsorge'

const TABS: Array<{ key: FinanzTab; label: string }> = [
  { key: 'haushalt', label: 'Haushalt' },
  { key: 'vorsorge', label: 'Vorsorge' },
]

function FinanzplanungPage() {
  const qc = useQueryClient()
  const { data: meData, isLoading: meLoading } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => api.me(),
  })
  useMobilePageTitle('Finanzplanung')

  const me = meData?.user
  const isAdmin = me?.role === 'admin' || !!me?.roleObj?.permissions?.all

  // Admin-Sicht auf andere Mitarbeiter ('' = eigene Planung). Nur lesend —
  // Mutationen bleiben serverseitig owner-only (Inhaber-Entscheid 2026-07-18).
  const [viewUserId, setViewUserId] = useState('')
  const readOnly = viewUserId !== '' && viewUserId !== me?.id

  const [tab, setTab] = useState<FinanzTab>(() => {
    const saved = localStorage.getItem('finanzplanung-tab')
    return saved === 'vorsorge' ? 'vorsorge' : 'haushalt'
  })
  function switchTab(next: FinanzTab) {
    // iOS tippt den Tab ohne vorheriges Blur — Commit des aktiven Feldes
    // muss VOR dem Wechsel laufen (sonst geht die Eingabe stumm verloren).
    const el = document.activeElement
    if (el instanceof HTMLElement) el.blur()
    setTab(next)
    localStorage.setItem('finanzplanung-tab', next)
  }

  const { data: usersData } = useQuery({
    queryKey: ['users', 'all'],
    queryFn: () => api.listUsers(),
    enabled: !!me && isAdmin,
  })

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['finance', 'budget', viewUserId || 'me'],
    queryFn: () => api.getFinanceBudget(viewUserId || undefined),
    enabled: !!me,
  })

  const createItem = useMutation({
    mutationFn: (section: FinanceBudgetSection) =>
      api.createFinanceBudgetItem({ section, label: 'Neuer Posten' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['finance'] })
      notify.success('Posten hinzugefügt')
    },
    onError: (e: Error) => notify.error(e.message),
  })

  const patchItem = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: { label?: string; monthlyCents?: number } }) =>
      api.updateFinanceBudgetItem(id, patch),
    // Kein Erfolgs-Toast — Blur-Autosave wäre sonst eine Toast-Flut.
    onSuccess: () => qc.invalidateQueries({ queryKey: ['finance'] }),
    onError: (e: Error) => {
      notify.error(e.message)
      // Abgelehnten Wert nicht als scheinbar gespeichert stehen lassen —
      // Refetch setzt die (unfokussierten) Felder auf den Server-Stand zurück.
      qc.invalidateQueries({ queryKey: ['finance'] })
    },
  })

  const deleteItem = useMutation({
    mutationFn: (id: string) => api.deleteFinanceBudgetItem(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['finance'] })
      notify.success('Posten gelöscht')
    },
    onError: (e: Error) => notify.error(e.message),
  })

  const items = useMemo(() => data?.items ?? [], [data])
  const totals = useMemo(() => computeFinanceBudget(items), [items])
  const note = finanznote(totals)

  if (meLoading) return null
  if (!me) return <Navigate to="/login" />

  const viewedUser = usersData?.users.find((u) => u.id === viewUserId)

  return (
    <div className="space-y-4 animate-fade-in">
      {/* In-Content-Kopf: <640 übernimmt die Shell → phone:hidden. */}
      <div className="flex phone:hidden items-center justify-between gap-2 pb-3 border-b border-border-subtle">
        <h1 className="text-lg font-medium tracking-tight text-text-primary shrink-0">
          Finanzplanung
        </h1>
      </div>

      {isAdmin && (
        <div className="flex flex-wrap items-center gap-2">
          <Select
            value={viewUserId}
            onChange={(e) => setViewUserId(e.target.value)}
            className="h-9 w-64 max-w-full text-sm"
            aria-label="Mitarbeiter auswählen"
          >
            <option value="">Meine Planung</option>
            {(usersData?.users ?? [])
              .filter((u) => u.id !== me.id)
              .map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
          </Select>
          {readOnly && (
            <span className="inline-flex items-center gap-1.5 rounded-lg bg-bg-overlay/60 px-2.5 py-1.5 text-xs text-text-secondary">
              <Eye className="h-3.5 w-3.5" />
              Ansicht von {viewedUser?.name ?? '…'} — nur lesend
            </span>
          )}
        </div>
      )}

      {/* Tab-Leiste (Muster time-accounts): Haushalt | Vorsorge */}
      <div className="flex items-center gap-1 rounded-xl bg-bg-overlay/40 p-1 w-fit">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => switchTab(t.key)}
            className={cn(
              'rounded-lg px-3.5 py-1.5 text-sm transition-colors touch:min-h-11',
              tab === t.key
                ? 'bg-bg-elevated text-text-primary shadow-sm'
                : 'text-text-secondary hover:text-text-primary',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'vorsorge' ? (
        <VorsorgeTab viewUserId={viewUserId} readOnly={readOnly} />
      ) : isLoading ? (
        <p className="text-text-secondary">Lade …</p>
      ) : isError ? (
        <div className="rounded-2xl border border-dashed border-border-default bg-bg-elevated/30 px-6 py-16 text-center">
          <p className="text-text-secondary">Finanzdaten konnten nicht geladen werden.</p>
          <Button className="mt-4" variant="secondary" onClick={() => refetch()}>
            Erneut versuchen
          </Button>
        </div>
      ) : readOnly && !data?.profile ? (
        <div className="rounded-2xl border border-dashed border-border-default bg-bg-elevated/30 px-6 py-16 text-center">
          <p className="text-text-secondary">
            {viewedUser?.name ?? 'Diese Person'} hat die Finanzplanung noch nicht eingerichtet.
          </p>
        </div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-3">
          <div className="space-y-4 lg:col-span-2">
            {SECTIONS.map((section) => (
              <BudgetSection
                key={section.key}
                section={section}
                items={items.filter((i) => i.section === section.key)}
                readOnly={readOnly}
                onAdd={() => createItem.mutate(section.key)}
                onPatch={(id, patch) => patchItem.mutate({ id, patch })}
                onDelete={async (item) => {
                  const ok = await confirm({
                    title: 'Posten löschen?',
                    description: `„${item.label}“ wird aus der Haushaltsübersicht entfernt.`,
                    confirmLabel: 'Löschen',
                    variant: 'danger',
                  })
                  if (ok) deleteItem.mutate(item.id)
                }}
              />
            ))}
            <SummaryCard totals={totals} />
          </div>

          <div className="space-y-4">
            <FinanznoteCard grade={note?.grade ?? null} percent={note?.percent ?? null} />
            <DonutCard totals={totals} />
          </div>
        </div>
      )}
    </div>
  )
}

function BudgetSection({
  section,
  items,
  readOnly,
  onAdd,
  onPatch,
  onDelete,
}: {
  section: (typeof SECTIONS)[number]
  items: FinanceBudgetItem[]
  readOnly: boolean
  onAdd: () => void
  onPatch: (id: string, patch: { label?: string; monthlyCents?: number }) => void
  onDelete: (item: FinanceBudgetItem) => void
}) {
  const tone = TONE_CLASSES[section.tone]
  const sumCents = items.reduce((acc, i) => acc + i.monthlyCents, 0)
  return (
    <section className="overflow-hidden rounded-2xl border border-border-subtle bg-bg-elevated/40">
      <header className={cn('flex items-center justify-between gap-2 px-4 py-2.5', tone.header)}>
        <span className={cn('inline-flex items-center gap-2 text-sm font-medium', tone.title)}>
          <span className={cn('h-2 w-2 rounded-full', tone.dot)} />
          {section.title}
        </span>
        <span className="font-mono text-xs tabular-nums text-text-secondary">
          {formatCurrency(sumCents)}
        </span>
      </header>

      {items.length > 0 && (
        <div className="flex items-center gap-2 px-4 pt-2 pb-1 text-2xs uppercase tracking-wider text-text-tertiary">
          <span className="min-w-0 flex-1" />
          <span className="w-28 text-right">monatlich</span>
          <span className="w-24 text-right phone:hidden">jährlich</span>
          <span className="w-28 text-right phone:hidden">in 10 Jahren</span>
          {!readOnly && <span className="w-8" />}
        </div>
      )}

      <div className="divide-y divide-border-subtle">
        {items.map((item) => (
          <BudgetRow
            key={item.id}
            item={item}
            readOnly={readOnly}
            onPatch={(patch) => onPatch(item.id, patch)}
            onDelete={() => onDelete(item)}
          />
        ))}
      </div>

      {!readOnly && (
        <button
          type="button"
          onClick={onAdd}
          className="flex w-full items-center gap-1.5 px-4 py-2 text-xs text-text-tertiary transition-colors hover:bg-bg-overlay/40 hover:text-text-secondary"
        >
          <Plus className="h-3.5 w-3.5" /> Posten hinzufügen
        </button>
      )}
    </section>
  )
}

function BudgetRow({
  item,
  readOnly,
  onPatch,
  onDelete,
}: {
  item: FinanceBudgetItem
  readOnly: boolean
  onPatch: (patch: { label?: string; monthlyCents?: number }) => void
  onDelete: () => void
}) {
  const [label, setLabel] = useState(item.label)
  const [amount, setAmount] = useState(centsToInput(item.monthlyCents))
  const labelFocused = useRef(false)
  const amountFocused = useRef(false)

  // Server-State gewinnt nach Refetch — aber pro Feld und NIE während der
  // User darin tippt (Review-Fund: der Refetch nach dem Label-Save wischte
  // sonst die halb getippte Betrags-Eingabe im Nachbarfeld weg).
  useEffect(() => {
    if (!labelFocused.current) setLabel(item.label)
  }, [item.label])
  useEffect(() => {
    if (!amountFocused.current) setAmount(centsToInput(item.monthlyCents))
  }, [item.monthlyCents])

  // Jährlich/10-Jahre live aus der aktuellen Eingabe ableiten (Original:
  // jährlich = ×12, in 10 Jahren = ×120).
  const liveCents = parseCents(amount) ?? item.monthlyCents

  function commitLabel() {
    labelFocused.current = false
    const trimmed = label.trim()
    if (!trimmed) {
      setLabel(item.label)
      return
    }
    if (trimmed !== item.label) onPatch({ label: trimmed })
  }

  function commitAmount() {
    amountFocused.current = false
    const cents = parseCents(amount)
    if (cents === null) {
      setAmount(centsToInput(item.monthlyCents))
      return
    }
    if (cents > FINANCE_MAX_MONTHLY_CENTS) {
      // Client kennt das Server-Cap (shared) — sonst käme rohes Zod-JSON als 400.
      notify.error(`Betrag zu groß — maximal ${formatCurrency(FINANCE_MAX_MONTHLY_CENTS)}`)
      setAmount(centsToInput(item.monthlyCents))
      return
    }
    if (cents !== item.monthlyCents) onPatch({ monthlyCents: cents })
  }

  return (
    <div className="group flex items-center gap-2 px-4 py-1.5">
      {readOnly ? (
        <span className="min-w-0 flex-1 truncate text-sm text-text-primary">{item.label}</span>
      ) : (
        <input
          value={label}
          maxLength={FINANCE_MAX_LABEL_LENGTH}
          onFocus={() => {
            labelFocused.current = true
          }}
          onChange={(e) => setLabel(e.target.value)}
          onBlur={commitLabel}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur()
          }}
          aria-label="Bezeichnung"
          className="min-w-0 flex-1 border-b border-transparent bg-transparent text-sm text-text-primary transition-colors placeholder:text-text-tertiary focus:border-accent/50 focus:outline-none"
        />
      )}

      {readOnly ? (
        <span className="w-28 text-right font-mono text-sm tabular-nums text-text-primary">
          {formatCurrency(item.monthlyCents)}
        </span>
      ) : (
        <Input
          // text + inputMode statt type=number: de-Komma-Eingabe („12,50")
          // würde type=number stumm verwerfen (Review-Fund; Muster logbook.tsx).
          type="text"
          inputMode="decimal"
          placeholder="0,00"
          value={amount}
          onFocus={() => {
            amountFocused.current = true
          }}
          onChange={(e) => setAmount(e.target.value)}
          onBlur={commitAmount}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur()
          }}
          aria-label="Betrag monatlich in Euro"
          className="h-8 w-28 px-2 text-right text-sm tabular-nums"
        />
      )}

      <span className="w-24 text-right font-mono text-xs tabular-nums text-text-tertiary phone:hidden">
        {formatCurrency(liveCents * 12)}
      </span>
      <span className="w-28 text-right font-mono text-xs tabular-nums text-text-tertiary phone:hidden">
        {formatCurrency(liveCents * 120)}
      </span>

      {!readOnly && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onDelete}
          title="Posten löschen"
          className="h-8 w-8 shrink-0 p-0 opacity-0 transition-opacity group-hover:opacity-100 touch:opacity-100"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      )}
    </div>
  )
}

function SummaryCard({ totals }: { totals: ReturnType<typeof computeFinanceBudget> }) {
  const rows: Array<{ label: string; cents: number; emphasis?: boolean }> = [
    { label: 'Summe der Einnahmen', cents: totals.incomeCents },
    { label: 'Summe der Ausgaben', cents: -totals.expenseCents },
    { label: 'E-A (Übrig)', cents: totals.surplusCents },
    { label: 'Vermögensaufbau', cents: totals.wealthCents },
    { label: 'VA-Bilanz', cents: totals.vaBilanzCents, emphasis: true },
  ]
  return (
    <section className="overflow-hidden rounded-2xl border border-border-subtle bg-bg-elevated/40">
      <header className="flex items-center px-4 py-2.5">
        <span className="text-sm font-medium text-text-primary">Haushaltsübersicht</span>
      </header>
      <div className="flex items-center gap-2 px-4 pb-1 text-2xs uppercase tracking-wider text-text-tertiary">
        <span className="min-w-0 flex-1" />
        <span className="w-28 text-right">monatlich</span>
        <span className="w-28 text-right phone:hidden">jährlich</span>
        <span className="w-32 text-right phone:hidden">in 10 Jahren</span>
      </div>
      <div className="divide-y divide-border-subtle">
        {rows.map((row) => (
          <div
            key={row.label}
            className={cn('flex items-center gap-2 px-4 py-2', row.emphasis && 'bg-bg-overlay/30')}
          >
            <span
              className={cn(
                'min-w-0 flex-1 truncate text-sm',
                row.emphasis ? 'font-medium text-text-primary' : 'text-text-secondary',
              )}
            >
              {row.label}
            </span>
            {[1, 12, 120].map((factor, idx) => (
              <span
                key={factor}
                className={cn(
                  'text-right font-mono text-sm tabular-nums',
                  idx === 0 ? 'w-28' : idx === 1 ? 'w-28 phone:hidden' : 'w-32 phone:hidden',
                  row.emphasis
                    ? row.cents >= 0
                      ? 'font-medium text-success'
                      : 'font-medium text-danger'
                    : 'text-text-primary',
                )}
              >
                {formatCurrency(row.cents * factor)}
              </span>
            ))}
          </div>
        ))}
      </div>
    </section>
  )
}

function FinanznoteCard({ grade, percent }: { grade: string | null; percent: number | null }) {
  const [showScale, setShowScale] = useState(false)
  return (
    <section className="rounded-2xl border border-border-subtle bg-bg-elevated/40 p-4">
      <p className="text-sm font-medium text-text-primary">Deine Finanznote</p>
      <div className="mt-3 flex items-end justify-between gap-3">
        <span className="text-5xl font-semibold tracking-tight text-text-primary">
          {grade ?? '—'}
        </span>
        <div className="text-right">
          <p className="font-mono text-sm tabular-nums text-text-secondary">
            {percent === null
              ? 'Keine Einnahmen erfasst'
              : `Sparanteil ${formatNumber(percent, 1)} %`}
          </p>
          <p className="text-xs text-text-tertiary">Zielsparquote: {FINANZNOTE_TARGET_PERCENT} %</p>
        </div>
      </div>

      <button
        type="button"
        aria-expanded={showScale}
        onClick={() => setShowScale((v) => !v)}
        className="mt-4 flex w-full items-center justify-between rounded-lg px-2 py-1.5 text-xs text-text-tertiary transition-colors hover:bg-bg-overlay/40 hover:text-text-secondary"
      >
        E-A-Notenskala
        <ChevronDown
          className={cn('h-3.5 w-3.5 transition-transform', showScale && 'rotate-180')}
        />
      </button>
      {showScale && (
        <div className="mt-1 divide-y divide-border-subtle rounded-lg border border-border-subtle">
          {FINANZNOTE_SCALE.map((step) => (
            <div
              key={step.grade}
              className={cn(
                'flex items-center justify-between px-3 py-1 text-xs',
                step.minPercent === FINANZNOTE_TARGET_PERCENT && 'bg-success/10',
                grade === step.grade && 'bg-accent/10',
              )}
            >
              <span className="font-medium text-text-primary">{step.grade}</span>
              <span className="font-mono tabular-nums text-text-secondary">
                ab {step.minPercent} %
              </span>
            </div>
          ))}
        </div>
      )}
    </section>
  )
}

function DonutCard({ totals }: { totals: ReturnType<typeof computeFinanceBudget> }) {
  const colors = useThemeColors()
  const slices = [
    { name: 'Fest', value: totals.fixedCents, color: colors.danger },
    { name: 'Flüssig', value: totals.variableCents, color: colors.warning },
    { name: 'Sicherheit', value: totals.insuranceCents, color: colors.info },
    { name: 'Vermögensaufbau', value: totals.wealthCents, color: colors.success },
    { name: 'Übrig', value: Math.max(totals.surplusCents, 0), color: colors.textTertiary },
  ].filter((s) => s.value > 0)

  return (
    <section className="rounded-2xl border border-border-subtle bg-bg-elevated/40 p-4">
      <p className="text-sm font-medium text-text-primary">Verteilung der Einnahmen</p>
      {totals.incomeCents <= 0 || slices.length === 0 ? (
        <p className="mt-4 text-sm text-text-tertiary">
          Trage Einnahmen und Ausgaben ein, um die Verteilung zu sehen.
        </p>
      ) : (
        <>
          <div className="mt-2 h-52">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={slices}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius="45%"
                  outerRadius="80%"
                  paddingAngle={2}
                  isAnimationActive={false}
                  stroke="none"
                >
                  {slices.map((s) => (
                    <Cell key={s.name} fill={s.color} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: colors.tooltipBg,
                    border: `1px solid ${colors.tooltipBorder}`,
                    borderRadius: 8,
                    fontSize: 11,
                  }}
                  labelStyle={{ color: colors.textPrimary }}
                  formatter={(v) => [formatCurrency(Number(v)), '']}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-2 space-y-1">
            {slices.map((s) => (
              <div key={s.name} className="flex items-center gap-2 text-xs">
                <span className="h-2 w-2 rounded-full" style={{ backgroundColor: s.color }} />
                <span className="min-w-0 flex-1 truncate text-text-secondary">{s.name}</span>
                <span className="font-mono tabular-nums text-text-tertiary">
                  {formatNumber((s.value / totals.incomeCents) * 100, 0)} %
                </span>
                <span className="w-24 text-right font-mono tabular-nums text-text-primary">
                  {formatCurrency(s.value)}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </section>
  )
}
